import { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation, Link } from 'wouter';
import { apiClient, ApiError } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { Clock, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LogoLoader } from '@/components/ui/logo-loader';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { MobileAppRedirect } from '@/components/MobileAppRedirect';
import { isMobileDevice } from '@/utils/mobile-detection';
import { useAlert } from '@/hooks/use-alert';
import { 
  attemptAutoLogin, 
  saveLoginCredentials, 
  getUsernameSuggestions, 
  getSavedPassword, 
  getLastUsedLogin 
} from '@/lib/auth';
import { localizedFolwMarketingPageUrl } from '@/utils/folw-marketing-urls';

function buildLoginSchema(t: (key: string, opts?: any) => string) {
  return z.object({
    email: z.string({ message: t('pages.login:validation.email.required') })
      .min(1, t('pages.login:validation.email.required'))
      .email(t('pages.login:validation.email.invalid')),
    password: z.string({ message: t('pages.login:validation.password.required') })
      .min(1, t('pages.login:validation.password.required')),
    rememberMe: z.boolean().optional(),
    acceptLegal: z.boolean().refine((v) => v === true, {
      message: t('pages.login:legal.required'),
    }),
  });
}

type LoginFormData = z.infer<ReturnType<typeof buildLoginSchema>>;

export function LoginPage() {
  const { t, i18n } = useTranslation(['common', 'pages.login']);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSuggestions, setEmailSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isAutoLogging, setIsAutoLogging] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [isResending, setIsResending] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { alert, confirm } = useAlert();
  const inviteAlertShownRef = useRef(false);
  const verificationAlertShownRef = useRef(false);

  // Check if user is on mobile device
  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  const schema = useMemo(() => buildLoginSchema(t), [t, i18n.language]);
  const form = useForm<LoginFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: true,
      acceptLegal: false,
    },
    mode: 'onSubmit', // Validate on submit first to prevent unhandled rejections on blur
    reValidateMode: 'onBlur', // Then validate on blur after first submit attempt
  });

  // Reset form when schema changes to ensure translations are current
  useEffect(() => {
    form.clearErrors();
  }, [schema, form]);

  useEffect(() => {
    const suggestions = getUsernameSuggestions('');
    setEmailSuggestions(suggestions);
    
    // Check for invite success / verification required messages
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('message') === 'invite-accepted' && !inviteAlertShownRef.current) {
      inviteAlertShownRef.current = true;
      const showInviteAlert = async () => {
        await alert({
          title: t('pages.login:inviteAcceptedTitle'),
          message: t('pages.login:inviteAcceptedMessage'),
        });
      };
      void showInviteAlert();
    }

    if (urlParams.get('verification') === 'required' && !verificationAlertShownRef.current) {
      verificationAlertShownRef.current = true;
      const showVerificationAlert = async () => {
        await alert({
          title: t('pages.login:verification.required'),
          message: t('pages.login:verification.message'),
        });
      };
      void showVerificationAlert();
    }
  }, [alert, t]);

  // Email-Vorschläge bei Eingabe
  const handleEmailChange = (value: string) => {
    form.setValue('email', value);
    
    if (value.length > 0) {
      const suggestions = getUsernameSuggestions(value);
      setEmailSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
      
      const savedPassword = getSavedPassword(value);
      if (savedPassword) {
        form.setValue('password', savedPassword);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (email: string) => {
    form.setValue('email', email);
    const savedPassword = getSavedPassword(email);
    if (savedPassword) {
      form.setValue('password', savedPassword);
    }
    setShowSuggestions(false);
  };

  const getLoginErrorDetails = (error: unknown) => {
    if (error instanceof ApiError) {
      const errorCode = error.data?.error || error.data?.code;
      const message =
        typeof error.data?.message === 'string' && error.data.message.length > 0
          ? error.data.message
          : error.message;
      return { code: errorCode, message };
    }

    if (error instanceof Error) {
      return { code: undefined, message: error.message };
    }

    return { code: undefined, message: undefined };
  };

  const resolveLoginErrorMessage = (code?: string, message?: string) => {
    if (code === 'AUTH_DISABLED_IN_PRODUCTION') {
      return t('pages.login:authDisabled.message');
    }

    if (code === 'INVALID_CREDENTIALS') {
      return t('pages.login:wrongCredentials');
    }

    if (code === 'EMAIL_NOT_VERIFIED') {
      return t('pages.login:emailNotVerified');
    }

    if (message && message !== 'API request failed') {
      const normalized = message.trim();
      if (normalized.length > 0) {
        const lowered = normalized.toLowerCase();
        if (lowered.includes('failed to fetch') || lowered.includes('network')) {
          return t('pages.login:connectionError');
        }
        return normalized;
      }
    }

    return t('pages.login:connectionError');
  };

  const handleVerificationRequired = async (email?: string) => {
    if (!email) {
      await alert({
        title: t('pages.login:verification.required'),
        message: t('pages.login:verification.message'),
      });
      return;
    }

    const confirmed = await confirm({
      title: t('pages.login:verification.required'),
      message: t('pages.login:verification.message'),
      confirmText: t('pages.login:verification.resend'),
      cancelText: t('common:alerts.ok'),
    });

    if (confirmed) {
      await handleResendVerification(email);
    }
  };

  const handleResendVerification = async (email: string) => {
    if (!email || isResending) return;

    setIsResending(true);
    try {
      const response = await apiClient.resendVerificationEmail(email, i18n.language);
      if (response.success) {
        toast({
          title: t('pages.login:verification.resend'),
          description: t('pages.verifyEmail:resend.message'),
        });
      } else {
        toast({
          title: t('pages.verifyEmail:resend.error'),
          description: response.message || t('pages.verifyEmail:errors.verificationFailed'),
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Resend verification error:', error);
      toast({
        title: t('pages.verifyEmail:resend.error'),
        description: error?.message || t('pages.verifyEmail:errors.verificationFailed'),
        variant: 'destructive',
      });
    } finally {
      setIsResending(false);
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    try {
      // console.log('🔐 Login form submitted with data:', data);
      // console.log('🔐 Form validation passed, proceeding with login');
      setIsLoading(true);
      setShowSuggestions(false);
      
      // console.log('🔐 Calling apiClient.login...');
      const result = await apiClient.login(data.email, data.password, i18n.language);
      // console.log('🔐 Login result:', result);
        
      if (data.rememberMe) {
        saveLoginCredentials(
          data.email,
          data.password,
          result.userCode,
          result.user.id,
          result.user.displayName || result.user.username
        );
        
        const { setRememberMe } = await import('@/lib/auth');
        setRememberMe(
          result.userCode, 
          result.user.id, 
          result.user.displayName || result.user.username
        );
      }

      // Set dev access flag for successful login
      localStorage.setItem('devAccess', 'true');
      
      toast({
        title: t('pages.login:successTitle'),
        description: data.rememberMe
          ? t('pages.login:welcomeToastRemember', { name: result.user.displayName || result.user.username })
          : t('pages.login:welcomeToast', { name: result.user.displayName || result.user.username }),
        variant: 'success',
      });

      // Register device after successful login
      try {
        const { registerDeviceManually } = await import('@/hooks/use-device-tracking');
        await registerDeviceManually('login', '/login');
      } catch (error) {
        // Silently fail - device tracking should not break login
        console.error('Failed to register device after login:', error);
      }

      try {
        const languageCode = i18n.language?.split('-')[0];
        if (languageCode && ['en', 'de', 'tr'].includes(languageCode)) {
          await apiClient.updatePreferences({ preferredLanguage: languageCode });
        }
      } catch {
        // Non-blocking: preferred language sync retries on next language change.
      }

      // GA4: login_success (consent-gated in analytics module)
      try {
        const { trackEvent } = await import('@/lib/analytics');
        const { ANALYTICS_EVENTS } = await import('@mangelapp/shared');
        trackEvent(ANALYTICS_EVENTS.LOGIN_SUCCESS);
      } catch {
        // no-op
      }

      const provisioningStatus = (result.user as any)?.orgProvisioningStatus as
        | 'pending'
        | 'ready'
        | 'failed'
        | undefined;
      if (provisioningStatus === 'pending') {
        setLocation('/provisioning-pending');
        return;
      }
      if (provisioningStatus === 'failed') {
        setLocation('/provisioning-failed');
        return;
      }

      // Always route authenticated web users to the dashboard once provisioning is ready.
      setLocation('/dashboard');
    } catch (error) {
      console.error('🔐 Login error:', error);
      const { code, message } = getLoginErrorDetails(error);

      if (code === 'EMAIL_NOT_VERIFIED') {
        await handleVerificationRequired(data.email);
        return;
      }

      const displayMessage = resolveLoginErrorMessage(code, message);
      await alert({
        title: t('pages.login:errorTitle'),
        message: displayMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Show mobile app redirect if on mobile device
  if (isMobile) {
    return <MobileAppRedirect />;
  }

  return (
    <div className="auth-page">
      <Card className="w-full max-w-md relative card-container-muted card-container-no-shadow card-container-border-black">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher compact />
        </div>
        <CardHeader className="text-center pb-6 pt-8">
          <div className="flex flex-col items-center justify-center mb-4">
            <img 
              src="/images/icons/SquareLogo.svg" 
              alt="FOLW Logo" 
              className="h-16 w-16 mb-3"
            />
            <img 
              src="/images/logos/FolwLogoText.svg" 
              alt="FOLW" 
              className="h-8"
            />
          </div>
        </CardHeader>
        <CardContent className="px-8 pb-8">

          <Form {...form}>
            <form onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              // Manually validate using safeParse to prevent unhandled rejections
              const values = form.getValues();
              const result = schema.safeParse(values);
              
              if (!result.success) {
                // Clear previous errors and set new ones manually
                form.clearErrors();
                for (const issue of result.error.issues) {
                  const field = issue.path?.[0] as keyof LoginFormData;
                  if (field) {
                    form.setError(field, {
                      type: 'validation',
                      message: issue.message,
                    }, { shouldFocus: false });
                  }
                }
                return;
              }
              
              // If validation passes, call onSubmit
              onSubmit(result.data);
            }} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="relative">
                    <FormLabel className="text-form-label mb-1.5 block">{t('pages.login:email')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input 
                          {...field}
                          value={field.value}
                          onChange={(e) => handleEmailChange(e.target.value)}
                          onFocus={() => {
                            if (field.value.length > 0) {
                              const suggestions = getUsernameSuggestions(field.value);
                              setEmailSuggestions(suggestions);
                              setShowSuggestions(suggestions.length > 0);
                            }
                          }}
                          onBlur={() => {
                            setTimeout(() => setShowSuggestions(false), 200);
                          }}
                          placeholder="Anna" 
                          disabled={isLoading || isAutoLogging}
                          autoComplete="email"
                        />
                        
                        {showSuggestions && emailSuggestions.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
                            {emailSuggestions.map((email, index) => (
                              <button
                                key={index}
                                type="button"
                                className="w-full px-3 py-2 text-left text-xs font-medium hover:bg-gray-50 flex items-center space-x-2"
                                onClick={() => selectSuggestion(email)}
                              >
                                <User className="h-3 w-3 text-gray-400" />
                                <span>{email}</span>
                                <Clock className="h-3 w-3 text-gray-300 ml-auto" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-form-label mb-1.5 block">{t('pages.login:password')}</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="password" 
                        placeholder="••••••••" 
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="text-right">
                <Link href="/forgot-password" className="text-primary-blue hover:text-[#3a7bc8] text-xs font-medium">
                  {t('pages.login:forgotPassword')}
                </Link>
              </div>
              
              <FormField
                control={form.control}
                name="rememberMe"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isLoading || isAutoLogging}
                        className="h-4 w-4 rounded border-primary data-[state=checked]:bg-[var(--primary-blue)] data-[state=checked]:border-primary"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-remember-me cursor-pointer">
                        {t('pages.login:rememberMe')}
                      </FormLabel>
                    </div>
                  </FormItem>
                )}
              />

              {isAutoLogging && (
                <div className="flex items-center justify-center space-x-2 py-2 bg-blue-50 rounded-md">
                  <LogoLoader size="sm" />
                  <span className="text-xs font-medium text-blue-600">{t('pages.login:autoLogin')}</span>
                </div>
              )}

              <FormField
                control={form.control}
                name="acceptLegal"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <div className="flex flex-row items-start gap-3">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(c) => field.onChange(c === true)}
                          disabled={isLoading || isAutoLogging}
                          className="mt-0.5 h-4 w-4 shrink-0 rounded border-primary data-[state=checked]:bg-[var(--primary-blue)] data-[state=checked]:border-primary"
                        />
                      </FormControl>
                      <div className="text-xs leading-relaxed text-form-label">
                        <span>{t('pages.login:legal.lead')}</span>
                        <a
                          href={localizedFolwMarketingPageUrl(i18n.language, 'privacypolicy')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-blue underline hover:text-[#3a7bc8] font-medium"
                        >
                          {t('pages.login:legal.privacyLink')}
                        </a>
                        <span>{t('pages.login:legal.middle')}</span>
                        <a
                          href={localizedFolwMarketingPageUrl(i18n.language, 'generalterms')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-blue underline hover:text-[#3a7bc8] font-medium"
                        >
                          {t('pages.login:legal.termsLink')}
                        </a>
                        <span>{t('pages.login:legal.suffix')}</span>
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || isAutoLogging || !form.watch('acceptLegal')}
              >
                {isLoading 
                  ? t('pages.login:loggingIn') 
                  : isAutoLogging 
                    ? t('pages.login:autoLogin') 
                    : t('pages.login:login')}
              </Button>
            </form>
          </Form>
          

          
          <div className="text-center mt-6">
            <Link href="/register" className="text-primary-blue hover:text-[#3a7bc8] text-xs font-medium">
              {t('pages.login:registerNow')}
            </Link>
          </div>
        </CardContent>

      </Card>
    </div>
  );
}
